import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, BadgeCheck, ChevronDown, ChevronUp, MapPin, Star, WandSparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ShopStaff, type StaffAssignableRole, type StorefrontStatus } from "@/api";
import { sellerKeys, useSellerStaff } from "@/api/hooks/use-sellers";
import { storefrontKeys, useShopAnalytics, useShopRoute, useStorefrontProducts } from "@/api/hooks/use-storefronts";
import { useUsers } from "@/api/hooks/use-users";
import { StackedThumbs } from "@/components/stacked-thumbs";
import { ShopModeration } from "@/components/shop-moderation";
import { SellerPartnerPromo } from "@/components/seller-partner-promo";
import { SellerAdPoints } from "@/components/seller-ad-points";
import { StaffPanel } from "@/components/staff-panel";
import { VerificationChecklist } from "@/components/verification-checklist";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Skeleton } from "@osmix/ui/skeleton";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { formatPrice } from "@/lib/utils";
import { useAuth } from "@/store";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

/** Свёрнут ли блок аналитики магазина — общий выбор для всех магазинов. */
const ANALYTICS_COLLAPSED_KEY = "osmix-admin.shop-analytics.collapsed";
const STATUS_VARIANT: Record<StorefrontStatus, "secondary" | "destructive"> = {
  active: "secondary",
  blocked: "destructive"
};
export function ShopDetailPage() {
  const $ = _c(2);
  const {
    slug: t0
  } = useParams();
  const slug = t0 === undefined ? "" : t0;
  let t1;
  if ($[0] !== slug) {
    t1 = <ShopDetailContent slug={slug} showBack={true} />;
    $[0] = slug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function ShopDetailContent(t0) {
  const $ = _c(67);
  const {
    slug,
    shopId: shopIdProp,
    showBack: t1
  } = t0;
  const showBack = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const route = useShopRoute(shopIdProp);
  const shopKey = shopIdProp ? route.key : slug;
  let t2;
  if ($[0] !== shopKey) {
    t2 = [...storefrontKeys.all, "detail", shopKey];
    $[0] = shopKey;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = t2 as const;
  let t4;
  if ($[2] !== shopKey) {
    t4 = () => api.storefronts.get(shopKey);
    $[2] = shopKey;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = !!shopKey && route.ready && route.fetchStorefront;
  let t6;
  if ($[4] !== t3 || $[5] !== t4 || $[6] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const shopQuery = useQuery(t6);
  const shop = shopQuery.data ?? null;
  const analyticsShopId = shopIdProp ?? shop?.id ?? "";
  const productsQuery = useStorefrontProducts(shop?.id ?? "");
  let t7;
  if ($[8] !== productsQuery.data) {
    t7 = productsQuery.data ?? [];
    $[8] = productsQuery.data;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const products = t7;
  const usersQuery = useUsers();
  let t8;
  if ($[10] !== shop || $[11] !== usersQuery) {
    t8 = shop ? (usersQuery.data ?? []).find(u => u.shopId === shop.id) ?? null : null;
    $[10] = shop;
    $[11] = usersQuery;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const owner = t8;
  const staffQuery = useSellerStaff(owner?.id ?? "");
  const staff = staffQuery.data ?? null;
  const [actionError, setActionError] = useState(null);
  const t9 = useAuth(_temp);
  let t10;
  if ($[13] !== t9) {
    t10 = hasAdminPermission(t9, "action.catalog_import.run");
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const canImport = t10;
  const storefrontMissing = shopQuery.isError || !!shopIdProp && route.ready && !route.fetchStorefront;
  const loadError = productsQuery.error ?? usersQuery.error ?? staffQuery.error;
  const error = actionError ?? (loadError ? loadError.message : null);
  let t11;
  if ($[15] !== owner || $[16] !== queryClient) {
    t11 = async (memberId, role) => {
      if (!owner) {
        return;
      }
      ;
      try {
        const member = await api.sellers.updateStaffRole(owner.id, memberId, role);
        queryClient.setQueryData(sellerKeys.staff(owner.id), current => current ? {
          ...current,
          members: current.members.map(item => item.id === member.id ? member : item)
        } : current);
      } catch (t12) {
        const e = t12;
        setActionError(e instanceof Error ? e.message : String(e));
      }
    };
    $[15] = owner;
    $[16] = queryClient;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const updateStaffRole = t11;
  let t12;
  if ($[18] !== owner || $[19] !== queryClient) {
    t12 = async memberId_0 => {
      if (!owner) {
        return;
      }
      ;
      try {
        await api.sellers.removeStaffMember(owner.id, memberId_0);
        queryClient.setQueryData(sellerKeys.staff(owner.id), current_0 => current_0 ? {
          ...current_0,
          members: current_0.members.filter(item_0 => item_0.id !== memberId_0)
        } : current_0);
      } catch (t13) {
        const e_0 = t13;
        setActionError(e_0 instanceof Error ? e_0.message : String(e_0));
      }
    };
    $[18] = owner;
    $[19] = queryClient;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const removeStaffMember = t12;
  let t13;
  if ($[21] !== showBack || $[22] !== t) {
    t13 = showBack && <Button variant="outline" size="sm" asChild={true}><Link to="/shops"><ArrowLeft className="size-4" /> {t("storefronts.back")}</Link></Button>;
    $[21] = showBack;
    $[22] = t;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== shop || $[25] !== t) {
    t14 = shop && <Badge variant={STATUS_VARIANT[shop.status]}>{t(`storefronts.status.${shop.status}`)}</Badge>;
    $[24] = shop;
    $[25] = t;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== shop?.verified || $[28] !== t) {
    t15 = shop?.verified && <Badge variant="secondary" className="gap-1"><BadgeCheck className="size-3.5" />{t("sellers.verifiedBadge")}</Badge>;
    $[27] = shop?.verified;
    $[28] = t;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== canImport || $[31] !== shop) {
    t16 = shop && canImport && <Button variant="outline" size="sm" className="ml-auto" asChild={true}><Link to={`/catalog-import?shopId=${encodeURIComponent(shop.id)}`}><WandSparkles className="size-4" />Загрузить каталог (ИИ)</Link></Button>;
    $[30] = canImport;
    $[31] = shop;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t13 || $[34] !== t14 || $[35] !== t15 || $[36] !== t16) {
    t17 = <div className="flex flex-wrap items-center gap-3">{t13}{t14}{t15}{t16}</div>;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== error) {
    t18 = error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>;
    $[38] = error;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== analyticsShopId) {
    t19 = analyticsShopId && <ShopAnalyticsCard shopId={analyticsShopId} />;
    $[40] = analyticsShopId;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== error || $[43] !== shop || $[44] !== storefrontMissing || $[45] !== t) {
    t20 = !shop && !error && storefrontMissing && <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">{t("storefronts.detail.noStorefront", {
        defaultValue: "\u0423 \u044D\u0442\u043E\u0433\u043E \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0430 \u043D\u0435\u0442 \u043E\u043F\u0443\u0431\u043B\u0438\u043A\u043E\u0432\u0430\u043D\u043D\u043E\u0439 \u0432\u0438\u0442\u0440\u0438\u043D\u044B. \u0417\u0430\u043A\u0430\u0437\u044B \u0438 \u0444\u0438\u043D\u0430\u043D\u0441\u044B \u0443\u0447\u0438\u0442\u044B\u0432\u0430\u044E\u0442\u0441\u044F, \u043D\u043E \u043F\u0443\u0431\u043B\u0438\u0447\u043D\u043E\u0439 \u043A\u0430\u0440\u0442\u043E\u0447\u043A\u0438 \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u0430 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442."
      })}</div>;
    $[42] = error;
    $[43] = shop;
    $[44] = storefrontMissing;
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== error || $[48] !== shop || $[49] !== storefrontMissing) {
    t21 = !shop && !error && !storefrontMissing && <div className="space-y-4"><Skeleton className="h-8 w-2/3" /><Skeleton className="h-48 w-full" /></div>;
    $[47] = error;
    $[48] = shop;
    $[49] = storefrontMissing;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== navigate || $[52] !== owner || $[53] !== products || $[54] !== removeStaffMember || $[55] !== shop || $[56] !== staff || $[57] !== t || $[58] !== updateStaffRole) {
    t22 = shop && <><header className="space-y-2"><h1 className="text-2xl font-semibold">{shop.name}</h1><p className="text-muted-foreground">{shop.tagline}</p><div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground"><span className="inline-flex items-center gap-1"><MapPin className="size-4" /> {shop.location}</span><span className="inline-flex items-center gap-1"><Star className="size-4 fill-current text-amber-500" />{shop.rating.toFixed(1)} · {shop.ratingCount}</span><span>{t("storefronts.detail.since", {
              year: shop.since
            })}</span><span>{t("storefronts.detail.productCount", {
              count: shop.productCount
            })}</span>{owner && <span>{t("storefronts.detail.owner")}:{" "}<Link to={`/users/${owner.id}`} className="text-primary hover:underline">{owner.name}</Link></span>}</div></header>{shop.verification && <VerificationChecklist verification={shop.verification} />}{owner ? <><ShopModeration userId={owner.id} /><SellerPartnerPromo userId={owner.id} /><SellerAdPoints userId={owner.id} /><StaffPanel staff={staff} editable={true} onRoleChange={updateStaffRole} onRemove={removeStaffMember} /></> : <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">{t("storefronts.detail.noOwner")}</div>}<ShopPartnersCard shopId={shop.id} /><ShopPickupPointsCard shopId={shop.id} /><Card><CardHeader><CardTitle>{t("storefronts.detail.about")}</CardTitle></CardHeader><CardContent><p className="whitespace-pre-line text-sm">{shop.description}</p></CardContent></Card><Card><CardHeader><CardTitle>{t("storefronts.detail.products")}</CardTitle></CardHeader><CardContent>{products.length === 0 ? <p className="text-sm text-muted-foreground">{t("storefronts.detail.noProducts")}</p> : <Table><TableHeader><TableRow><TableHead className="w-[64px]">{t("products.col.photo")}</TableHead><TableHead>{t("products.col.name")}</TableHead><TableHead>{t("products.col.brand")}</TableHead><TableHead className="text-right">{t("products.col.price")}</TableHead><TableHead className="text-right">{t("products.col.stock")}</TableHead></TableRow></TableHeader><TableBody>{products.map(p => <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/products/${p.id}`)}><TableCell>{p.photos[0] ? <img src={p.photos[0]} alt="" className="size-10 rounded object-cover" /> : <div className="size-10 rounded bg-muted" />}</TableCell><TableCell className="font-medium">{p.name}</TableCell><TableCell className="text-muted-foreground">{p.brand}</TableCell><TableCell className="text-right tabular-nums">{formatPrice(p.price)}</TableCell><TableCell className="text-right tabular-nums">{p.stock}</TableCell></TableRow>)}</TableBody></Table>}</CardContent></Card></>;
    $[51] = navigate;
    $[52] = owner;
    $[53] = products;
    $[54] = removeStaffMember;
    $[55] = shop;
    $[56] = staff;
    $[57] = t;
    $[58] = updateStaffRole;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== t17 || $[61] !== t18 || $[62] !== t19 || $[63] !== t20 || $[64] !== t21 || $[65] !== t22) {
    t23 = <div className="space-y-6">{t17}{t18}{t19}{t20}{t21}{t22}</div>;
    $[60] = t17;
    $[61] = t18;
    $[62] = t19;
    $[63] = t20;
    $[64] = t21;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  return t23;
}

// Богатая карточка аналитики магазина: ключевые метрики, спарклайн выручки за
// 30 дней, топ товаров и последние заказы с миниатюрами. Данные — из
// GET /admin/shops/:shopId/analytics (живой SQL по заказам).
function _temp(s) {
  return s.user;
}
function ShopAnalyticsCard({
  shopId
}: {
  shopId: string;
}) {
  const {
    t
  } = useTranslation();
  const {
    data: a,
    isLoading,
    error
  } = useShopAnalytics(shopId);
  const money = (n: number) => `${Math.round(n).toLocaleString("ru-RU")} ₽`;
  // Блок метрик сворачивается: на длинной странице магазина важное есть и
  // сверху, и снизу, а развёрнутая аналитика отодвигает таблицы за экран.
  // Выбор запоминается — иначе его пришлось бы повторять на каждом магазине.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(ANALYTICS_COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(ANALYTICS_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      /* приватный режим — состояние просто не переживёт перезагрузку */
    }
  }, [collapsed]);
  if (isLoading) return <Card>
				<CardContent className="p-4">
					<Skeleton className="h-24 w-full" />
				</CardContent>
			</Card>;
  if (error || !a) return null;
  const max = Math.max(1, ...a.revenueSeries.map(p => p.value));
  const pts = a.revenueSeries.length;
  return <Card>
			<CardHeader className="flex flex-row items-center justify-between gap-2">
				<CardTitle>
					{t("storefronts.analytics.title", {
          defaultValue: "Аналитика магазина"
        })}
				</CardTitle>
				<Button variant="ghost" size="sm" onClick={() => setCollapsed(v => !v)} aria-expanded={!collapsed}>
					{collapsed ? <>
							<ChevronDown className="mr-1 size-4" />
							{t("common.expand", {
            defaultValue: "Развернуть"
          })}
						</> : <>
							<ChevronUp className="mr-1 size-4" />
							{t("common.collapse", {
            defaultValue: "Свернуть"
          })}
						</>}
				</Button>
			</CardHeader>
			{collapsed ? null : <CardContent className="flex flex-col gap-4">
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
					<Metric label={t("finances.gmv", {
          defaultValue: "Оборот"
        })} value={money(a.gmv)} />
					<Metric label={t("storefronts.analytics.orders", {
          defaultValue: "Заказы"
        })} value={`${a.ordersPaid}${a.ordersCancelled > 0 ? ` (+${a.ordersCancelled})` : ""}`} />
					<Metric label={t("finances.aov", {
          defaultValue: "Средний чек"
        })} value={money(a.avgCheck)} />
					<Metric label={t("finances.commission", {
          defaultValue: "Комиссия"
        })} value={money(a.commission)} />
					<Metric label={t("storefronts.analytics.buyers", {
          defaultValue: "Покупатели"
        })} value={String(a.buyersCount)} />
					<Metric label={t("storefronts.analytics.returns", {
          defaultValue: "Возвраты"
        })} value={`${a.returnsCount} · ${Math.round(a.returnRate * 100)}%`} />
				</div>

				{/* Каталог рядом с деньгами: «три карточки и куча продаж» и «пятьсот
        карточек и три продажи» — разные проблемы, и видеть их надо сразу. */}
				{a.catalog && <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
						<Metric label={t("storefronts.analytics.productsFilled", {
          defaultValue: "Карточек опубликовано"
        })} value={`${a.catalog.productsPublished} из ${a.catalog.productsTotal}`} />
						<Metric label={t("storefronts.analytics.productsModeration", {
          defaultValue: "На модерации"
        })} value={`${a.catalog.productsModeration}${a.catalog.productsRejected > 0 ? ` · откл. ${a.catalog.productsRejected}` : ""}`} />
						<Metric label={t("storefronts.analytics.unitsSold", {
          defaultValue: "Продано единиц"
        })} value={String(a.catalog.unitsSold)} />
						<Metric label={t("storefronts.analytics.stockUnits", {
          defaultValue: "Остаток на складе"
        })} value={String(a.catalog.stockUnits)} />
						<Metric label={t("storefronts.analytics.rating", {
          defaultValue: "Рейтинг магазина"
        })} value={a.catalog.reviewsCount > 0 ? `${a.catalog.rating.toFixed(1)} · ${a.catalog.reviewsCount}` : "—"} />
					</div>}

				<div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
					{a.createdAt && <span>
							{t("storefronts.analytics.created", {
            defaultValue: "Создан"
          })}:{" "}
							{new Date(a.createdAt).toLocaleDateString("ru-RU")}
						</span>}
					{a.lastOrderAt && <span>
							{t("storefronts.analytics.lastOrder", {
            defaultValue: "Последний заказ"
          })}:{" "}
							{new Date(a.lastOrderAt).toLocaleDateString("ru-RU")}
							{a.daysSinceLastOrder != null ? ` (${a.daysSinceLastOrder} дн.)` : ""}
						</span>}
				</div>

				{/* Спарклайн выручки за 30 дней */}
				{a.gmv > 0 && <div>
						<p className="mb-1 text-xs text-muted-foreground">
							{t("finances.dailyGmv", {
            defaultValue: "Оборот по дням"
          })}
						</p>
						<svg viewBox={`0 0 ${pts} 40`} preserveAspectRatio="none" className="h-16 w-full">
							<polyline fill="none" stroke="currentColor" strokeWidth="1" className="text-primary" points={a.revenueSeries.map((p_0, i) => `${i},${40 - p_0.value / max * 38}`).join(" ")} />
						</svg>
					</div>}

				{/* grid-cols-1 = minmax(0, 1fr): без него колонка на телефоне
        растягивалась под длинное имя товара (nowrap у truncate) и
        страница уезжала вбок до 664 px. */}
				<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
					{a.topProducts.length > 0 && <div className="min-w-0">
							<p className="mb-2 text-sm font-medium">
								{t("storefronts.analytics.topProducts", {
              defaultValue: "Топ товаров"
            })}
							</p>
							<ul className="space-y-1 text-sm">
								{a.topProducts.map(p_1 => <li key={p_1.name} className="flex justify-between gap-2">
										<span className="min-w-0 truncate text-muted-foreground">{p_1.name}</span>
										<span className="shrink-0 tabular-nums">
											{p_1.qty} · {money(p_1.revenue)}
										</span>
									</li>)}
							</ul>
						</div>}

					{a.recentOrders.length > 0 && <div className="min-w-0">
							<p className="mb-2 text-sm font-medium">
								{t("storefronts.analytics.recentOrders", {
              defaultValue: "Последние заказы"
            })}
							</p>
							<Table>
								<TableBody>
									{a.recentOrders.map(o => <TableRow key={o.id}>
											<TableCell className="p-2">
												<StackedThumbs items={o.thumbs.map(url => ({
                    photo: url,
                    name: ""
                  }))} />
											</TableCell>
											<TableCell className="p-2 text-xs text-muted-foreground">{o.buyerName}</TableCell>
											<TableCell className="p-2 text-right tabular-nums">{money(o.total)}</TableCell>
										</TableRow>)}
								</TableBody>
							</Table>
						</div>}
				</div>
			</CardContent>}
		</Card>;
}
function Metric(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <div className="text-base font-semibold tabular-nums">{value}</div>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <div className="text-xs text-muted-foreground">{label}</div>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="rounded-lg border p-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * Партнёрские магазины (26.09.2026). Набор задаёт продавец в бутике; площадке
 * важно видеть связи — например, перед блокировкой: партнёрский блок пропадёт
 * и у тех, кто этот магазин показывает. Только чтение.
 */
function ShopPartnersCard(t0) {
  const $ = _c(11);
  const {
    shopId
  } = t0;
  let t1;
  let t2;
  if ($[0] !== shopId) {
    t1 = ["storefronts", "partners", shopId];
    t2 = () => api.storefronts.partners(shopId);
    $[0] = shopId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = !!shopId;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const query = useQuery(t4);
  const data = query.data;
  const list = _temp8;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CardHeader><CardTitle>Партнёрские магазины</CardTitle><p className="text-sm text-muted-foreground">Набор задаёт продавец в разделе «Бутик» кабинета.</p></CardHeader>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== data || $[9] !== query.error) {
    t6 = <Card data-testid="shop-partners">{t5}<CardContent className="grid gap-4 md:grid-cols-2">{query.error ? <p className="text-sm text-destructive">{(query.error as Error).message}</p> : !data ? <p className="text-sm text-muted-foreground">Загрузка…</p> : <><div className="flex flex-col gap-2"><p className="text-xs uppercase tracking-wider text-muted-foreground">Показывает в своём бутике</p>{list(data.partners)}</div><div className="flex flex-col gap-2"><p className="text-xs uppercase tracking-wider text-muted-foreground">Показывают этот магазин</p>{list(data.partnerOf)}</div></>}</CardContent></Card>;
    $[8] = data;
    $[9] = query.error;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}

/**
 * Самовывоз от продавца (17.10.2026): точки выдачи магазина — адрес, график,
 * сроки и стоимость, как их видит покупатель. Заводит продавец в «Доставке»
 * кабинета; здесь только чтение — для поддержки и разбора споров по выдаче.
 */
function _temp8(items) {
  return items.length === 0 ? <p className="text-sm text-muted-foreground">Нет</p> : <ul className="flex flex-col gap-1 text-sm">{items.map(_temp7)}</ul>;
}
function _temp7(item) {
  return <li key={item.shopId} className="flex items-center justify-between gap-3"><Link to={`/shops/${item.slug}`} className="text-primary hover:underline">{item.name}</Link><span className="text-xs text-muted-foreground tabular-nums">товаров: {item.productCount}</span></li>;
}
function ShopPickupPointsCard(t0) {
  const $ = _c(16);
  const {
    shopId
  } = t0;
  let t1;
  let t2;
  if ($[0] !== shopId) {
    t1 = ["storefronts", "pickup-points", shopId];
    t2 = () => api.storefronts.pickupPoints(shopId);
    $[0] = shopId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = !!shopId;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const query = useQuery(t4);
  const data = query.data;
  const ready = _temp9;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CardTitle>Точки самовывоза</CardTitle>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = data ? data.enabled ? "\u0421\u0430\u043C\u043E\u0432\u044B\u0432\u043E\u0437 \u0432\u043A\u043B\u044E\u0447\u0451\u043D." : "\u0421\u0430\u043C\u043E\u0432\u044B\u0432\u043E\u0437 \u0432\u044B\u043A\u043B\u044E\u0447\u0435\u043D." : "";
  let t7;
  if ($[8] !== t6) {
    t7 = <CardHeader>{t5}<p className="text-sm text-muted-foreground">{t6} Точки заводит продавец в разделе «Доставка» кабинета.</p></CardHeader>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== data || $[11] !== query.error) {
    t8 = <CardContent>{query.error ? <p className="text-sm text-destructive">{(query.error as Error).message}</p> : !data ? <p className="text-sm text-muted-foreground">Загрузка…</p> : data.points.length === 0 ? <p className="text-sm text-muted-foreground">Нет</p> : <ul className="grid gap-3 md:grid-cols-2">{data.points.map(p_0 => <li key={p_0.id} className="flex flex-col gap-1 rounded-md border p-3 text-sm"><div className="flex items-start justify-between gap-2"><span className="font-medium">{p_0.name}</span>{!p_0.enabled && <Badge variant="outline">Скрыта</Badge>}</div><span>{[p_0.city, p_0.address].filter(Boolean).join(", ")}</span>{p_0.schedule && <span className="text-muted-foreground">{p_0.schedule}</span>}{p_0.phone && <span className="text-muted-foreground">{p_0.phone}</span>}<span className="text-xs text-muted-foreground">Готовность {ready(p_0)} · хранение {p_0.storageDays} дн. · {p_0.price === 0 ? "\u0431\u0435\u0441\u043F\u043B\u0430\u0442\u043D\u043E" : formatPrice(p_0.price)}</span>{p_0.instructions && <span className="text-xs text-muted-foreground">Как найти: {p_0.instructions}</span>}</li>)}</ul>}</CardContent>;
    $[10] = data;
    $[11] = query.error;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = <Card data-testid="shop-pickup-points">{t7}{t8}</Card>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
function _temp9(p) {
  return p.readyAmount <= 0 ? "\u0441\u0440\u0430\u0437\u0443" : `через ${p.readyAmount} ${p.readyUnit === "hours" ? "\u0447" : "\u0434\u043D."}`;
}